import { React, useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import RevenueChart from './RevenueChart';
import SalesHistory from './SalesHistory';

import LoadingAnimation from '../../../Animation/LoadingAnimation.json';

export default function Revenue(props) {
  const {
    today,
    calcDateText,

    totalMaxPrice,
    revenue,
    prevRevenue,

    selectedPartnerGym,
    isOpenPartnerGymSales,

    isRevenueLoading,
    setIsRevenueLoading,

    salesData,
    isSalesLoading,
    setIsSalesLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [thisYear, setThisYear] = useState([]);
  const [thisYearMaxPrice, setThisYearMaxPrice] = useState(0);
  const [viewMode, setViewMode] = useState('year');

  // 월별 매출 데이터 집계 //
  useEffect(() => {
    const months = Array.from({ length: 12 }, () => ({
      totalPrice: 0,
      cashPrice: 0,
      cardPrice: 0,
      discountPrice: 0,
      notReceived: 0,
      prevTotalPrice: 0,
      sales: [],
      selectedPaymentOptions: []
    }));

    let maxPrice = 0;

    for (const sale of revenue) {
      const monthIndex = parseInt(sale.approvalDate.split('T')[0].split('-')[1], 10) - 1;

      if (monthIndex < 0 || monthIndex > 11) continue;

      const month = months[monthIndex];

      month.totalPrice += sale.totalPrice;
      month.cashPrice += sale.cashPrice;
      month.cardPrice += sale.cardPrice;
      month.notReceived += sale.notReceivedPrice;
      month.sales.push(sale);

      // 결제 옵션 집계 //
      for (const option of sale.selectedPaymentOptions) {
        if (option.paymentOptionId === 0) continue;

        const existing = month.selectedPaymentOptions.find(
          o => o.paymentOptionId === option.paymentOptionId
        );

        if (existing) {
          existing.price += option.price;
        }
        else {
          month.selectedPaymentOptions.push({
            paymentOptionId: option.paymentOptionId,
            paymentOptionName: option.paymentOptionName,
            price: option.price
          });
        }
      }

      month.selectedPaymentOptions.sort((a, b) => a.paymentOptionId - b.paymentOptionId);

      if (month.totalPrice > maxPrice) maxPrice = month.totalPrice;
    }

    // 전년도 매출 집계 //
    for (const sale of prevRevenue) {
      const monthIndex = parseInt(sale.approvalDate.split('T')[0].split('-')[1], 10) - 1;

      if (monthIndex >= 0 && monthIndex <= 11) {
        months[monthIndex].prevTotalPrice += sale.totalPrice;
      }
    }

    setThisYear(months);
    setThisYearMaxPrice(maxPrice);

    setTimeout(() => setIsRevenueLoading(false), 1000);
  }, [revenue, prevRevenue, today]);

  // 매출 증감 계산 (viewMode에 따라 YOY / MOM 분기) //
  function calcChange() {
    if (thisYear.length === 0) return { diff: 0, percent: null, direction: 'none' };

    const currentIndex = today.month();
    const current = thisYear[currentIndex]?.totalPrice || 0;
    let prev = 0;

    if (viewMode === 'year') {
      prev = thisYear[currentIndex]?.prevTotalPrice || 0;
    }
    else {
      const prevIndex = currentIndex - 1;

      if (prevIndex >= 0) prev = thisYear[prevIndex]?.totalPrice || 0;
    }

    if (prev === 0) return { diff: current, percent: null, direction: 'none' };

    const diff = current - prev;
    const percent = Math.round((diff / prev) * 100);
    const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'same';

    return { diff, percent, direction };
  }

  // 증감(+/-) 텍스트 결정 //
  function renderYoyText() {
    const { diff, percent, direction } = calcChange();

    if (direction === 'none' && diff === 0) {
      return (
        <YoyTextWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
            {t('statistics.chart.noStandard')}
          </Text>
        </YoyTextWrapper>
      );
    }

    const sign = diff >= 0 ? '+ ' : '- ';
    const diffText = sign + Math.abs(diff).toLocaleString('ko-KR') + t('statistics.won');
    const percentText = percent !== null ? ' (' + (diff >= 0 ? '+' : '') + percent + '%)' : '';
    const label = diff > 0 ? t('statistics.revenue.higher') : diff < 0 ? t('statistics.revenue.lower') : t('statistics.revenue.same');
    const accentColor = diff >= 0 ? theme.primary : theme.pointBlue;
    const comparedLabel = viewMode === 'year' ? t('statistics.revenue.comparedToLastYear') : t('statistics.revenue.comparedToLastMonth');

    return (
      <YoyTextWrapper>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
          {comparedLabel}
        </Text>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
          {diffText}{percentText}
        </Text>
        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
          {label}
        </Text>
      </YoyTextWrapper>
    );
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isRevenueLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
        isOpenPartnerGymSales ?
          <>
            <HeaderWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                {today.clone().format(t('statistics.dateFormatMonth'))}
              </Text>
              <PriceWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                  {(thisYear[today.month()]?.totalPrice || 0).toLocaleString('ko-KR')}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text} cursor='default'>
                  {t('statistics.won')}
                </Text>
              </PriceWrapper>
            </HeaderWrapper>

            <ChartSection>
              <ChartHeaderWrapper>
                {renderYoyText()}

                <LegendWrapper>
                  <LegendItem>
                    <LegendBar backgroundColor={theme.primary} />
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
                      {t('statistics.revenue.thisYearSales')}
                    </Text>
                  </LegendItem>
                  <LegendItem>
                    <LegendDashedBar $color={theme.subText} />
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} cursor='default'>
                      {t('statistics.revenue.lastYearSales')}
                    </Text>
                  </LegendItem>
                </LegendWrapper>
              </ChartHeaderWrapper>

              <RevenueChart
                thisYear={thisYear}
                thisYearMaxPrice={thisYearMaxPrice}
              />

              <ViewModeToggleWrapper>
                <ViewModeButton
                  $isActive={viewMode === 'month'}
                  $isDark={isDark}
                  onClick={() => setViewMode('month')}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'month' ? theme.white : theme.text} cursor='pointer'>
                    {t('statistics.revenue.oneMonth')}
                  </Text>
                </ViewModeButton>
                <ViewModeButton
                  $isActive={viewMode === 'year'}
                  $isDark={isDark}
                  onClick={() => setViewMode('year')}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={viewMode === 'year' ? theme.white : theme.text} cursor='pointer'>
                    {t('statistics.revenue.oneYear')}
                  </Text>
                </ViewModeButton>
              </ViewModeToggleWrapper>
            </ChartSection>

            <SalesHistory
              calcDateText={calcDateText}
              data={salesData}
              selectedPartnerGym={selectedPartnerGym}
              isOpenPartnerGymSales={isOpenPartnerGymSales}
              isSalesLoading={isSalesLoading}
              setIsSalesLoading={setIsSalesLoading}
            />
          </>
          :
          <NotOpenTextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivate')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>
              {t('statistics.partnerGymPrivateDesc')}
            </Text>
          </NotOpenTextWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 20px 40px;

  width: calc(100% - 80px);
  max-width: 940px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: ${props => props.$isDark ? 'none' : '0px 40px 50px rgba(240, 232, 227, 0.5)'};
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 400px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 80px;
  height: 80px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;
`;

const ChartSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const ChartHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const YoyTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const LegendBar = styled.div`
  width: 40px;
  height: 7px;

  border-radius: 2px;
  background-color: ${props => props.backgroundColor};
`;

const LegendDashedBar = styled.div`
  width: 40px;
  height: 0px;

  border-top: 2px dashed ${props => props.$color};
`;

const ViewModeToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin: 20px auto 0 auto;

  padding: 5px;

  width: 201px;
  height: 31px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};

  gap: 5px;
`;

const ViewModeButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 96px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.theme.primary : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px 0px rgba(0, 0, 0, 0.05)' : 'none'};

  cursor: pointer;

  transition: all 0.3s ease-in-out;
`;

const NotOpenTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;